import React, { useEffect, useCallback, useState } from "react";
import { observer } from "mobx-react";
import { useLocation } from "react-router-dom";
import { CircularProgress, Pagination } from "@ann/web-components";
import { v4 as uuidv4 } from "uuid";
import { useToast } from "../../../../../components/toast";
import { MainWrapper, MainContainer, HeaderFiltersContainer, DepartmentsContainer, DepartmentsGridWrapper, LoadingContainer, PaginationContainer, CustomGenericListStyle } from "./Main.styled";
import { Header, Filters, DepartmentCard, CreateDepartmentModal } from "./components";
import { useStore } from "../../../../../hooks";
import { createDepartment } from "../../../../../services/departments";

const getOwnerUuid = (value) => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  if (uuidRegex.test(value)) {
    return value;
  }
  const hash = value.split('').reduce((acc, char) => {
    return ((acc << 5) - acc) + char.charCodeAt(0);
  }, 0);
  return `11111111-2222-3333-4444-${Math.abs(hash).toString().padStart(12, '0')}`;
};

const generateCodeFromName = (name) => {
  return name
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
};

const Main = ({ breadcrumbHelpers }) => {
  const { departmentsStore } = useStore();
  const location = useLocation();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const toast = useToast();
  
  const allDepartments = departmentsStore.departmentList;
  const existingDepartmentNames = allDepartments
    .map((dept) => dept.department_name || dept.name || '')
    .filter(Boolean);

  useEffect(() => {
    departmentsStore.resetPagination();
    departmentsStore.fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const isMainRoute = location.pathname === '/v2/departments' || location.pathname === '/v2/departments/';
    if (isMainRoute) {
      breadcrumbHelpers?.clear?.();
    }
  }, [location.pathname, breadcrumbHelpers]);

  const handlePageChange = useCallback((page) => {
    departmentsStore.setCurrentPage(page);
    departmentsStore.fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleItemsPerPageChange = useCallback((itemsPerPage) => {
    departmentsStore.setItemsPerPage(itemsPerPage);
    departmentsStore.fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchChange = useCallback((value) => {
    const searchParams = value ? { keyword: value } : {};
    departmentsStore.setSearchParams(searchParams);
    departmentsStore.fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSortChange = useCallback((sortOption) => {
    departmentsStore.setSortParams({
      order_by: [sortOption],
      order_direction: departmentsStore.sortParams.order_direction,
    });
    departmentsStore.fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOrderChange = useCallback((currentOrder) => {
    const newOrder = currentOrder === 'asc' ? 'desc' : 'asc';
    departmentsStore.setSortParams({
      order_by: departmentsStore.sortParams.order_by,
      order_direction: [newOrder.toUpperCase()],
    });
    departmentsStore.fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateDepartment = useCallback(async (departmentData) => {
    try {
      if (!departmentData.adminOwner?.value) {
        toast.error({
          title: "Validation Error",
          message: "Admin Owner is required",
        });
        return;
      }

      const params = {
        code: generateCodeFromName(departmentData.name),
        name: departmentData.name,
        description: departmentData.description || null,
        ownerUuid: getOwnerUuid(departmentData.adminOwner.value),
        departmentUuid: uuidv4(),
      };

      const response = await createDepartment(params);

      const statusCode = response?.status_code || response?.statusCode;
      const isValidResponse = statusCode === 200 || statusCode === 201;

      if (!isValidResponse) {
        const errorMessage = response?.msg_code?.[0]?.description || 
                             response?.msgCode?.[0]?.description || 
                             'Failed to create department';
        toast.error({
          title: "Error",
          message: errorMessage,
        });
        return;
      }

      const createdDepartment = response.body || {};

      setIsCreateModalOpen(false);
      
      toast.success({
        title: "Department created successfully",
        message: `${createdDepartment.name || departmentData.name} has been created`,
      });
      
      departmentsStore.resetPagination();
      await departmentsStore.fetchPage();
    } catch (error) {
      console.error("Error creating department:", error);
      toast.error({
        title: "Error",
        message: error.message || "Failed to create department",
      });
    }
  }, [toast, departmentsStore]);

  return (
    <>
    <CustomGenericListStyle />
    <MainWrapper>
      <HeaderFiltersContainer>
        <Header onCreateClick={() => setIsCreateModalOpen(true)} />
        <Filters 
          onSearchChange={handleSearchChange}
          onSortChange={handleSortChange}
          onOrderChange={handleOrderChange}
          selectedSortOption={departmentsStore.sortParams.order_by?.[0] || "created_at"}
          orderDirection={departmentsStore.sortParams.order_direction?.[0]?.toLowerCase() || "desc"}
          loading={departmentsStore.isLoading}
        />
      </HeaderFiltersContainer>
      <MainContainer>
        <DepartmentsContainer>
          {departmentsStore.isLoading && allDepartments.length === 0 ? (
            <LoadingContainer>
              <CircularProgress size="xl" />
            </LoadingContainer>
          ) : allDepartments.length === 0 ? (
            <div>
              <p>No departments found</p>
            </div>
          ) : (
            <>
              {departmentsStore.isLoading && allDepartments.length > 0 && (
                <LoadingContainer>
                  <CircularProgress size="xl" />
                </LoadingContainer>
              )}
              
              {!departmentsStore.isLoading && (
                <DepartmentsGridWrapper>
                  {allDepartments.map((department) => (
                    <DepartmentCard
                      key={department.department_id || department.id}
                      department={department}
                    />
                  ))}
                </DepartmentsGridWrapper>
              )}
            </>
          )}
        </DepartmentsContainer>
        {allDepartments.length > 0 && (
          <PaginationContainer>
            <Pagination
              currentPage={departmentsStore.currentPage}
              itemsPerPage={departmentsStore.itemsPerPage}
              onItemsPerPageChange={handleItemsPerPageChange}
              onPageChange={handlePageChange}
              totalItems={departmentsStore.meta?.total_items || 0}
              totalPages={departmentsStore.meta?.total_pages || 1}
            />
          </PaginationContainer>
        )}
      </MainContainer>
    </MainWrapper>
    
    <CreateDepartmentModal 
      isOpen={isCreateModalOpen}
      onClose={() => setIsCreateModalOpen(false)}
      onCreateDepartment={handleCreateDepartment}
      existingNames={existingDepartmentNames}
    />
    </>
  );
};  

export default observer(Main);

